import { useState } from "react"
import { Screen } from "./App"
import {
  ApiError,
  createUser,
  fitnessOnDemandSaveLink,
  gympassActivate,
  promoCodeRedeem,
  promoLinkRedeem,
} from "./api"
import { useT } from "@transifex/react"

export const PasswordCreateScreen = ({
  assignedLocale,
  email,
  promoCode,
  gympassToken,
  fitnessOnDemandToken,
  promoLinkToken,
  onComplete,
  onFailure,
}: {
  assignedLocale: string | undefined
  email: string
  promoCode: string | undefined
  gympassToken: string | undefined
  fitnessOnDemandToken: string | undefined
  promoLinkToken: string | undefined
  onComplete: (nextScreen: Screen, rewardDuration?: string) => void
  onFailure: (nextScreen: Screen, errorMessage?: string) => void
}) => {
  const t = useT()
  const [password, setPassword] = useState("")
  const [repeatPassword, setRepeatPassword] = useState("")
  const [inputError, setInputError] = useState<string>()
  const [apiError, setApiError] = useState<string>()
  const [loading, setLoading] = useState(false)

  const createUserAndRedeem = async () => {
    setApiError(undefined)
    if (password.length < 7) {
      setInputError(t("Password must be at least 7 characters"))
      return
    }
    if (repeatPassword !== password) {
      setInputError(t("Passwords must match"))
      return
    }
    setLoading(true)
    try {
      await createUser(email, password, assignedLocale)
      if (promoCode) {
        const rewardDuration = await promoCodeRedeem(promoCode)
        onComplete(Screen.SuccessCreated, rewardDuration)
      } else if (gympassToken) {
        await gympassActivate(gympassToken)
        onComplete(Screen.SuccessCreated)
      } else if (fitnessOnDemandToken) {
        await fitnessOnDemandSaveLink(fitnessOnDemandToken)
        onComplete(Screen.SuccessCreated)
      } else if (promoLinkToken) {
        await promoLinkRedeem(promoLinkToken)
        onComplete(Screen.SuccessCreated)
      } else {
        throw new Error("Bug: pass a token")
      }
    } catch (error) {
      if (error instanceof ApiError) {
        onFailure(Screen.Failed, error.message)
      } else {
        setApiError(t("Something went wrong, please try again."))
      }
    } finally {
      setLoading(false)
    }
  }

  let submitText
  if (promoCode) {
    submitText = t("Create account and redeem code")
  } else if (gympassToken) {
    submitText = t("Create account and activate Wellhub")
  } else if (fitnessOnDemandToken) {
    submitText = t("Create account and link Fitness On Demand")
  } else if (promoLinkToken) {
    submitText = t("Create account and get Motosumo Premium")
  }

  return (
    <>
      {apiError !== undefined && <p className="App-api-error">{apiError}</p>}
      <p className="App-description">
        {t("Create a password for your new\nMotosumo account.")}
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          createUserAndRedeem()
        }}
      >
        <input
          className="App-input password"
          type="password"
          value={password}
          placeholder={t("Password")}
          onChange={(event) => {
            setPassword(event.target.value)
            setInputError(undefined)
          }}
        />
        <input
          className="App-input repeat-password"
          type="password"
          value={repeatPassword}
          placeholder={t("Repeat password")}
          onChange={(event) => {
            setRepeatPassword(event.target.value)
            setInputError(undefined)
          }}
        />
        {inputError !== undefined && (
          <p className="App-input-error">{inputError}</p>
        )}
        {loading ? (
          <div className="loader" />
        ) : (
          <input
            className="App-button"
            type="submit"
            value={submitText}
            disabled={loading}
          />
        )}
      </form>
    </>
  )
}
